import React from "react";
import PageShell, { SectionHeader } from "@/components/PageShell";
import { LESSONS, TIMELINE, PORTRAITS } from "@/data/content";
import { Link } from "react-router-dom";
import NextRoom from "@/components/NextRoom";

export default function About() {
  return (
    <PageShell
      testId="page-about"
      title="About | Quentin Young"
      description="The background, lessons, philosophy, and evolving body of work behind Quentin Young: Canadian entrepreneur, founder, operator, brand strategist, and creative leader."
      ogImage="/og-about.jpg"
      breadcrumbs={[{ name: "Home", url: "/" }, { name: "About", url: "/about" }]}
    >
      {/* Header */}
      <section className="container-edge pt-16 md:pt-24 pb-16">
        <p className="eyebrow-gold mb-5">About</p>
        <h1 className="display-serif text-4xl md:text-6xl lg:text-7xl text-paper-primary text-balance">
          A person studying people, organizations, systems, and shared achievement.
        </h1>
        <p className="mt-8 max-w-2xl font-serif text-lg md:text-xl text-paper-secondary leading-relaxed text-pretty">
          This is not a résumé. It is the record of how a way of seeing was earned, slowly,
          across rooms most of the world never glances at.
        </p>
      </section>

      {/* Portrait + intro */}
      <section className="container-edge pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border border-ink-line bg-ink-surface">
              <img src={PORTRAITS.seatedWindow} alt="Quentin Young seated by a window, looking outward." className="w-full h-full object-cover img-desaturate" />
            </div>
            <p className="mt-4 eyebrow">Portrait No. 03, Window</p>
          </div>
          <div className="lg:col-span-7">
            <p className="eyebrow-gold mb-4">Introduction</p>
            <p className="font-serif text-2xl md:text-3xl text-paper-primary leading-snug text-pretty">
              Raised in the company of standards I did not choose, then taught to choose them on purpose.
            </p>
            <div className="mt-8 space-y-5 text-paper-secondary leading-relaxed text-lg">
              <p>
                The work began with grocery carts and ended up at the boardroom in less time than it
                should have. Every step taught something the step before could not.
              </p>
              <p>
                What is published here is what survived contact with reality, the lessons that paid
                for themselves more than once and the ideas that earned the right to a permanent address.
              </p>
              <p>
                I do not write to instruct. I write to remember, refine, and connect. If something
                here also helps you see your team, your organization, or yourself more clearly, then
                the field notes are doing what they were meant to do.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lessons That Built Me */}
      <section className="bg-ink-surface border-y border-ink-line py-24 md:py-36">
        <div className="container-edge">
          <SectionHeader
            eyebrow="Lessons That Built Me"
            title="Most lessons arrive disguised as mistakes."
            lead="Each line below is evidence. None of it is melodrama. The teachers were the rooms, and the rooms were rarely kind."
          />
          <ol className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-line">
            {LESSONS.map((l, i) => (
              <li key={`${l.era}-${i}`} className="bg-ink-base p-8 md:p-10" data-testid={`lesson-${i}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">
                    {String(i + 1).padStart(2, "0")} · {l.era}
                  </p>
                </div>
                <h3 className="mt-3 font-serif text-2xl text-paper-primary">{l.title}</h3>
                <p className="mt-3 text-paper-secondary text-base leading-relaxed">{l.lesson}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-ink-base py-24 md:py-36">
        <div className="container-edge">
          <SectionHeader eyebrow="Timeline" title="A chronology of thought formation." lead="Not a job list. A record of where the work was earned." />
          <ol className="mt-16 grid gap-px bg-ink-line">
            {TIMELINE.map((t, i) => (
              <li key={`${t.year}-${i}`} className="bg-ink-base">
                <div className="grid grid-cols-12 gap-6 items-start py-8 md:py-10 px-2 md:px-4 border-t border-transparent">
                  <div className="col-span-12 md:col-span-2">
                    <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-gold-warm">{t.year}</p>
                  </div>
                  <div className="col-span-12 md:col-span-4">
                    <h3 className="font-serif text-2xl text-paper-primary">{t.title}</h3>
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <p className="text-paper-secondary leading-relaxed">{t.note}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Linkouts */}
      <section className="bg-ink-base py-16 border-t border-ink-line">
        <div className="container-edge grid grid-cols-1 md:grid-cols-3 gap-px bg-ink-line">
          <Link to="/philosophy" className="bg-ink-base p-10 group" data-testid="about-link-philosophy">
            <p className="eyebrow">Continue</p>
            <p className="mt-3 font-serif text-3xl text-paper-primary group-hover:text-gold-warm transition-colors">The framework →</p>
          </Link>
          <Link to="/building" className="bg-ink-base p-10 group" data-testid="about-link-building">
            <p className="eyebrow">Continue</p>
            <p className="mt-3 font-serif text-3xl text-paper-primary group-hover:text-gold-warm transition-colors">What is being built →</p>
          </Link>
          <Link to="/acknowledgements" className="bg-ink-base p-10 group" data-testid="about-link-ack">
            <p className="eyebrow">Continue</p>
            <p className="mt-3 font-serif text-3xl text-paper-primary group-hover:text-gold-warm transition-colors">Whose shoulders →</p>
          </Link>
        </div>
      </section>

      <NextRoom
        eyebrow="Move to the next room"
        title="Work with Quentin, or go deeper."
        rooms={[
          { label: "Work with Quentin", to: "/coaching", hint: "coaching" },
          { label: "Invite Quentin to speak", to: "/speaking", hint: "speaking" },
          { label: "Explore the thinking", to: "/thinking", hint: "the ideas" },
        ]}
        testId="about-next"
      />
    </PageShell>
  );
}
